import { gsap } from 'gsap';
import Draggable from 'gsap/dist/Draggable';
import React, { useState, useCallback, useContext, useEffect, useRef } from 'react';
import { ThemeProvider } from 'styled-components';
import { TabsProps, TypesIconsType } from './types';
import {
  ContentSection,
  ContentWrapper,
  RenderTab,
  Slider,
  SliderContainer,
  TabsContainer,
  TabsSection,
  TabsWrapper,
} from './styles';
import { ThemeContext } from '../ThemeProvider/ThemeProvider';
import ComplexIcon from '../ComplexIcon/ComplexIcon';
import BasicIcon from '../BasicIcon/BasicIcon';
import { SizesType } from '../ThemeProvider/types';
import { Icons } from '../BasicIcon/types';
import { Icons as ComplexIconsType } from '../ComplexIcon/types';

gsap.registerPlugin(Draggable);

const GAP = 16;

const Tabs: React.FC<TabsProps> = (props) => {
  const {
    initialIndex = 0,
    children,
    height,
    onChange,
    tabs,
    fullWidth,
    typeIcons = 'complex',
    size = 'large',
    align = 'left',
    adjustableWidth = false,
    width,
    swipeable = false,
  } = props;
  const [index, setIndex] = useState(initialIndex);
  const containerRef = useRef(null);
  const sliderRef = useRef(null);
  const indexRef = useRef<number>(initialIndex);
  const dragRef = useRef<Draggable[] | null>(null);

  const handleTabChange = useCallback(
    (newIndex: number, disabled: boolean) => () => {
      if (!disabled) {
        if (onChange) onChange(() => setIndex(newIndex), newIndex);
        else setIndex(newIndex);
      }
    },
    [onChange],
  );

  useEffect(() => {
    setIndex(initialIndex);
  }, [initialIndex]);

  useEffect(() => {
    indexRef.current = index;
  }, [index]);

  const { actualTheme } = useContext(ThemeContext);

  const positions = React.Children.map(children, (child) => child);
  const content = React.Children.map(positions[0], (child) => {
    if (React.isValidElement(child)) {
      return child.props.children;
    }
    return child;
  });

  const renderBasicIcon = useCallback(
    (tab, i) => (
      <BasicIcon
        name={tab.icon as Icons}
        clickable
        disabled={tab.disabled}
        size={`large` as SizesType}
        color={index === i ? actualTheme.tabs.active : actualTheme.gray4D}
      />
    ),
    [actualTheme, index, size],
  );

  const renderComplexIcon = useCallback(
    (tab, i) => (
      <ComplexIcon
        name={tab.icon as ComplexIconsType}
        clickable
        disabled={tab.disabled}
        size={`tab-size-${size}` as SizesType}
        color={index === i ? actualTheme.tabs.active : actualTheme.gray4D}
      />
    ),
    [actualTheme, index, size],
  );

  const renderIcon: { [key in TypesIconsType]: Function } = {
    basic: renderBasicIcon,
    complex: renderComplexIcon,
  };

  const onDragEnd = useCallback(() => {
    const delta = dragRef.current[0].endX - dragRef.current[0].startX;
    const containerWidth = containerRef.current.offsetWidth + GAP;

    if (delta < -100 && indexRef.current < tabs.length - 1) {
      setIndex((prev) => prev + 1);
    } else if (delta > 100 && indexRef.current > 0) {
      setIndex((prev) => prev - 1);
    }
    gsap.to(sliderRef.current, { x: -indexRef.current * containerWidth, duration: 0.3 });
  }, [tabs]);

  useEffect(() => {
    if (!sliderRef.current || !containerRef.current) return;

    const updateDraggable = () => {
      const containerWidth = containerRef.current.offsetWidth + GAP;
      const minX = -(tabs.length - 1) * containerWidth;
      gsap.to(sliderRef.current, { x: -indexRef.current * containerWidth, duration: 0.3 });

      if (dragRef.current) {
        dragRef.current[0].kill();
      }

      dragRef.current = Draggable.create(sliderRef.current, {
        type: 'x',
        edgeResistance: 0.7,
        bounds: { maxX: 0, minX },
        inertia: true,
        onDragEnd,
      });
    };

    updateDraggable();

    const resizeObserver = new ResizeObserver(() => {
      updateDraggable();
    });

    resizeObserver.observe(containerRef.current);

    // eslint-disable-next-line consistent-return
    return () => {
      resizeObserver.disconnect();
      if (dragRef.current) dragRef.current[0].kill();
    };
  }, [onDragEnd, tabs]);

  useEffect(() => {
    if (containerRef.current) {
      const containerWidth = containerRef.current.offsetWidth + GAP;
      gsap.to(sliderRef.current, { x: -index * containerWidth, duration: 0.3 });
    }
  }, [index]);

  return (
    <ThemeProvider theme={actualTheme}>
      <TabsWrapper height={swipeable ? '100%' : height || 'auto'}>
        <TabsContainer>
          <TabsSection>
            {(tabs as any[]).map((tab, i) => (
              <>
                <RenderTab
                  disabled={tab.disabled}
                  onClick={handleTabChange(i, tab.disabled)}
                  active={index === i}
                  index={i}
                  length={tabs.length}
                  fullWidth={fullWidth}
                  width={width}
                  size={size}
                  align={align}
                  adjustableWidth={adjustableWidth}
                  data-testid={tab.testId}
                >
                  {size !== 'small' &&
                    size !== 'minimal' &&
                    tab.icon &&
                    renderIcon[typeIcons](tab, i)}
                  {tab.title}
                </RenderTab>
              </>
            ))}
          </TabsSection>
          <ContentSection swipeable={swipeable}>
            {!swipeable &&
              content.map((cont, i) => (
                <div>
                  {i === index && (
                    <ContentWrapper data-testid="tab-content-test">{cont}</ContentWrapper>
                  )}
                </div>
              ))}
            {swipeable && (
              <SliderContainer ref={containerRef}>
                <Slider ref={sliderRef}>
                  {content.map((cont) => (
                    <ContentWrapper data-testid="tab-content-test" swipeable>
                      {cont}
                    </ContentWrapper>
                  ))}
                </Slider>
              </SliderContainer>
            )}
          </ContentSection>
        </TabsContainer>
      </TabsWrapper>
    </ThemeProvider>
  );
};

export default Tabs;
